import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { supabase, safeChannel } from "./supabase";

// useMyKidRequests — the kid's OWN active food requests, surfaced
// for the "My Asks" strip on the kid's Foods tab. Pending + seen
// only (done / dismissed drop off automatically). Realtime sub so
// status flips from the parent's inbox (or a cook_session_id link)
// appear on the kid's iPad within a second.

// Active = still on the strip as pending/coming/cooking. We ALSO pull
// recently-finished, cook-linked requests so the strip can show a
// brief 🎉 "You got it!" celebration after the parent's cook finishes
// (trigger 0267 flips them to 'done'), then they age out.
const CELEBRATE_WINDOW_MS = 4 * 60 * 60 * 1000;

export function useMyKidRequests(kidUserId) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const chRef = useRef(null);

  const load = useCallback(async () => {
    if (!kidUserId) { setRows([]); setLoading(false); return; }
    setLoading(true);
    const celebrateCutoff = new Date(Date.now() - CELEBRATE_WINDOW_MS).toISOString();
    const { data, error } = await supabase
      .from("kid_food_requests")
      .select("id, recipe_slug, canonical_id, food_name, food_emoji, request_kind, status, cook_session_id, requested_at, resolved_at")
      .eq("kid_user_id", kidUserId)
      // pending/seen (active) OR a just-completed cooked request.
      .or(`status.in.(pending,seen),and(status.eq.done,cook_session_id.not.is.null,resolved_at.gte.${celebrateCutoff})`)
      .order("requested_at", { ascending: false })
      .limit(20);
    if (error) {
      console.warn("[my-kid-requests] load:", error.message);
    }
    setRows(data || []);
    setLoading(false);
  }, [kidUserId]);

  useEffect(() => { load(); }, [load]);

  // Realtime on kid pseudo-users is unreliable (RLS / publication), so
  // the parent flipping a request to "seen"/"cooking" can fail to
  // reach the kid — leaving them with no sign their ask was accepted.
  // Belt-and-braces: refresh when the kid returns to the app and on a
  // gentle interval so the status updates within seconds regardless.
  useEffect(() => {
    if (!kidUserId) return undefined;
    const onVis = () => { if (document.visibilityState === "visible") load(); };
    document.addEventListener("visibilitychange", onVis);
    const id = setInterval(load, 15000);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      clearInterval(id);
    };
  }, [kidUserId, load]);

  useEffect(() => {
    if (!kidUserId) return;
    const ch = safeChannel(`rt:my_kid_requests:${kidUserId}`)
      .on("postgres_changes",
        { event: "*", schema: "public", table: "kid_food_requests", filter: `kid_user_id=eq.${kidUserId}` },
        () => load())
      .subscribe();
    chRef.current = ch;
    return () => {
      if (chRef.current) {
        supabase.removeChannel(chRef.current);
        chRef.current = null;
      }
    };
  }, [kidUserId, load]);

  // Decorated rows with a derived display state:
  //   "pending"  — kid asked, parent hasn't acknowledged
  //   "accepted" — parent flipped status='seen' in the inbox
  //   "cooking"  — accepted AND cook_session_id is set (parent
  //                started cooking this request's recipe)
  //   "celebrate" — cook finished; show 🎉 "You got it!" briefly
  const decorated = useMemo(() => rows.map((r) => {
    let displayState = "pending";
    if (r.status === "done") {
      displayState = "celebrate";
    } else if (r.status === "seen") {
      displayState = r.cook_session_id ? "cooking" : "accepted";
    }
    return { ...r, displayState };
  }), [rows]);

  return { rows: decorated, loading, refresh: load };
}
